import React, { useState } from 'react';
import { formatDate } from '../../lib/formatDate';
import { errorMessage, toastError, toastSuccess } from '../../lib/toast';
import { markReviewed } from './vendorsApi';
import {
  REVIEW_OUTCOME_LABEL, RISK_LABEL, VENDOR_ERRORS,
} from './vendorLabels';
import { Dialog } from './VendorBits';

const NOTE_MAX = 1000;

/**
 * Records a periodic review (approve level). The risk level is asked for only
 * when the vendor has none: the next review date depends on it, and a vendor
 * that already has one keeps it.
 */
export default function MarkReviewedDialog({ vendor, onClose, onDone }) {
  const needsRisk = !vendor.risk_level;
  const [outcome, setOutcome] = useState('satisfactory');
  const [note, setNote] = useState('');
  const [risk, setRisk] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const found = {};
    if (!note.trim()) found.note = 'Add a note about the review.';
    if (needsRisk && !risk) found.risk = VENDOR_ERRORS.risk_required;
    setErrors(found);
    if (Object.keys(found).length) return;

    const body = { outcome, note: note.trim() };
    if (needsRisk) body.risk_level = risk;
    setBusy(true);
    try {
      const res = await markReviewed(vendor.id, body);
      toastSuccess(`Marked reviewed — next review on ${formatDate(res?.next_review)}.`);
      onDone(res);
    } catch (err) {
      toastError(errorMessage(err, 'Could not record the review.', VENDOR_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Mark ${vendor.name} reviewed`} onClose={onClose} labelledBy="vd-review-title">
      <form onSubmit={submit} noValidate>
        <fieldset className="vd-fieldset">
          <legend className="vd-list-meta">Outcome</legend>
          {Object.entries(REVIEW_OUTCOME_LABEL).map(([key, label]) => (
            <label key={key} className="vd-radio">
              <input type="radio" name="vd-outcome" value={key} checked={outcome === key} onChange={() => setOutcome(key)} />
              {label}
            </label>
          ))}
        </fieldset>
        {needsRisk && (
          <div className="vd-form-row">
            <label htmlFor="vd-review-risk">Risk level</label>
            <select id="vd-review-risk" value={risk} onChange={(e) => setRisk(e.target.value)}>
              <option value="">Select a risk level</option>
              {Object.entries(RISK_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            {errors.risk && <p className="vd-field-error" role="alert">{errors.risk}</p>}
          </div>
        )}
        <div className="vd-form-row">
          <label htmlFor="vd-review-note">Note</label>
          <textarea id="vd-review-note" rows={4} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} />
          <span className="vd-counter">{note.length}/{NOTE_MAX}</span>
          {errors.note && <p className="vd-field-error" role="alert">{errors.note}</p>}
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>Record review</button>
        </div>
      </form>
    </Dialog>
  );
}
